import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ShieldAlert, DollarSign, Users, Activity, Ban, CheckCircle2, XCircle } from "lucide-react";
import {
  powerAdminConfig, powerAdminUpdateConfig,
  powerAdminWithdrawals, powerAdminApproveW, powerAdminRejectW,
  powerAdminFraud, powerAdminStats,
} from "@/lib/powerApi";

const REWARD_LABELS = {
  signature:       "Signed a petition",
  create:          "Created a petition",
  creator_signed:  "Someone signed your petition",
  daily_checkin:   "Daily check-in",
  referral_joined: "Friend joined via your link",
  achievement:     "Achievement unlocked",
  rewarded_ad:     "Rewarded ad completed",
};

export default function PowerAdminTab() {
  const [tab, setTab] = useState("stats");

  return (
    <div data-testid="admin-power-tab">
      <div className="glass rounded-2xl p-5 mb-6 border border-[#1E88FF]/30 bg-gradient-to-br from-[#1E88FF]/10 via-transparent to-transparent">
        <div className="mono text-[10px] uppercase tracking-widest text-[#1E88FF] mb-1">// jixxeo power · admin</div>
        <div className="font-heading font-black uppercase tracking-tighter text-2xl">Jixxeo Power controls</div>
        <div className="text-white/70 text-sm mt-1">Tune reward payouts, approve withdrawals, and monitor fraud signals.</div>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        {[
          { id: "stats", label: "Stats" },
          { id: "config", label: "Rewards config" },
          { id: "withdrawals", label: "Withdrawals" },
          { id: "fraud", label: "Fraud signals" },
        ].map((t) => (
          <button key={t.id} data-testid={`padmin-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={"mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full transition-colors " + (tab === t.id ? "bg-[#1E88FF] text-black" : "border border-white/10 text-white/70 hover:border-white/30")}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "stats" && <StatsPanel />}
      {tab === "config" && <ConfigPanel />}
      {tab === "withdrawals" && <WithdrawalsPanel />}
      {tab === "fraud" && <FraudPanel />}
    </div>
  );
}

function StatsPanel() {
  const [s, setS] = useState(null);
  useEffect(() => { powerAdminStats().then(setS).catch(() => toast.error("Couldn't load stats")); }, []);
  if (!s) return <div className="text-white/50 text-sm">Loading…</div>;
  const cards = [
    { label: "Petitions total", value: s.petitions.total, icon: <Activity className="w-4 h-4" /> },
    { label: "Petitions active", value: s.petitions.active, icon: <Activity className="w-4 h-4" /> },
    { label: "Signatures total", value: s.signatures.total.toLocaleString(), icon: <Users className="w-4 h-4" /> },
    { label: "Signatures today", value: s.signatures.today.toLocaleString(), icon: <Users className="w-4 h-4" /> },
    { label: "Comments total", value: s.comments.total.toLocaleString(), icon: <Activity className="w-4 h-4" /> },
    { label: "Withdrawals pending", value: s.withdrawals.pending, icon: <DollarSign className="w-4 h-4" /> },
    { label: "Withdrawals paid $", value: `$${s.withdrawals.paid_total_usd.toFixed(2)}`, icon: <DollarSign className="w-4 h-4" /> },
    { label: "Promo revenue $", value: `$${s.revenue.promotions_usd.toFixed(2)}`, icon: <DollarSign className="w-4 h-4" /> },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="padmin-stats">
      {cards.map((c) => (
        <div key={c.label} className="glass rounded-xl p-4">
          <div className="mono text-[10px] uppercase tracking-widest text-white/50 inline-flex items-center gap-1">{c.icon}{c.label}</div>
          <div className="font-heading font-black text-2xl mt-1 text-white">{c.value}</div>
        </div>
      ))}
    </div>
  );
}

function ConfigPanel() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    powerAdminConfig().then((c) => {
      setCfg(c);
      setForm({
        ...c.rewards,
        min_withdraw_usd: c.min_withdraw_usd,
        withdraw_fee_usd: c.withdraw_fee_usd,
        signup_bonus_usd: c.signup_bonus_usd,
      });
    }).catch(() => toast.error("Couldn't load config"));
  }, []);

  if (!cfg) return <div className="text-white/50 text-sm">Loading…</div>;

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        rewards: Object.fromEntries(Object.keys(cfg.rewards).map((k) => [k, Number(form[k] || 0)])),
        min_withdraw_usd: Number(form.min_withdraw_usd),
        withdraw_fee_usd: Number(form.withdraw_fee_usd),
        signup_bonus_usd: Number(form.signup_bonus_usd),
      };
      const r = await powerAdminUpdateConfig(payload);
      // PATCH returns the effective config without `defaults` — keep the
      // client's cached `defaults` so the hint text keeps rendering.
      setCfg((prev) => ({ ...(prev || {}), ...r, defaults: (prev && prev.defaults) || r.defaults || {} }));
      toast.success("Reward settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="padmin-config" className="space-y-5">
      <div className="glass rounded-xl p-4">
        <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-3">// per-action reward amounts (usd)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Object.keys(cfg.rewards).map((k) => (
            <label key={k} className="block">
              <div className="text-xs text-white/70 mb-1">{REWARD_LABELS[k] || k}</div>
              <div className="flex items-center gap-2">
                <span className="text-white/50 mono text-xs">$</span>
                <input data-testid={`padmin-reward-${k}`} type="number" step="0.001" min="0" max="10"
                  value={form[k] ?? cfg.rewards[k]}
                  onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                  className="w-32 px-3 py-1.5 bg-white/5 border border-white/10 rounded text-sm text-white" />
                <span className="text-white/40 mono text-[10px]">default: ${cfg.defaults[k]}</span>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="glass rounded-xl p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <label>
          <div className="text-xs text-white/70 mb-1">Minimum withdrawal (USD)</div>
          <input data-testid="padmin-min-withdraw" type="number" step="0.5" min="0"
            value={form.min_withdraw_usd ?? cfg.min_withdraw_usd}
            onChange={(e) => setForm((f) => ({ ...f, min_withdraw_usd: e.target.value }))}
            className="w-full px-3 py-1.5 bg-white/5 border border-white/10 rounded text-sm" />
        </label>
        <label>
          <div className="text-xs text-white/70 mb-1">Withdrawal fee (USD)</div>
          <input data-testid="padmin-fee" type="number" step="0.05" min="0"
            value={form.withdraw_fee_usd ?? cfg.withdraw_fee_usd}
            onChange={(e) => setForm((f) => ({ ...f, withdraw_fee_usd: e.target.value }))}
            className="w-full px-3 py-1.5 bg-white/5 border border-white/10 rounded text-sm" />
        </label>
        <label>
          <div className="text-xs text-white/70 mb-1">Signup bonus (USD)</div>
          <input data-testid="padmin-signup-bonus" type="number" step="0.05" min="0"
            value={form.signup_bonus_usd ?? cfg.signup_bonus_usd}
            onChange={(e) => setForm((f) => ({ ...f, signup_bonus_usd: e.target.value }))}
            className="w-full px-3 py-1.5 bg-white/5 border border-white/10 rounded text-sm" />
        </label>
      </div>

      <button data-testid="padmin-config-save" onClick={save} disabled={busy}
        className="px-6 py-3 rounded-full bg-cyan-500 text-black font-black mono text-xs uppercase tracking-widest hover:bg-cyan-400 disabled:opacity-50 inline-flex items-center gap-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
        Save settings
      </button>

      <div className="text-[11px] text-white/50 mono">
        These amounts credit members' Power wallets silently on qualifying actions. The frontend UI never displays them to members — only the resulting balance and labeled ledger history.
      </div>
    </div>
  );
}

function WithdrawalsPanel() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await powerAdminWithdrawals(status);
      setRows(r.withdrawals || []);
    } catch { toast.error("Couldn't load withdrawals"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const act = async (id, kind) => {
    setBusyId(id);
    try {
      if (kind === "approve") await powerAdminApproveW(id);
      else await powerAdminRejectW(id);
      toast.success(kind === "approve" ? "Approved & marked paid" : "Rejected, funds returned");
      setRows((list) => list.filter((r) => r.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusyId(null); }
  };

  return (
    <div data-testid="padmin-withdrawals">
      <div className="flex gap-2 mb-4 flex-wrap">
        {["pending", "paid", "rejected", "all"].map((s) => (
          <button key={s} data-testid={`padmin-w-filter-${s}`} onClick={() => setStatus(s)}
            className={"mono text-[10px] uppercase tracking-widest px-3 py-1 rounded-full " + (status === s ? "bg-white text-black" : "border border-white/10 text-white/60 hover:border-white/30")}>
            {s}
          </button>
        ))}
      </div>
      {loading ? <div className="text-white/50 text-sm">Loading…</div> :
       rows.length === 0 ? <div className="glass rounded-xl p-6 text-center text-white/50 text-sm">No {status} withdrawals.</div> :
       <div className="space-y-2">
         {rows.map((w) => (
           <div key={w.id} data-testid={`padmin-w-${w.id}`} className="glass rounded-xl p-4 flex flex-wrap items-center gap-3">
             <div className="min-w-0 flex-1">
               <div className="font-heading font-bold text-white truncate">
                 {(w.user?.name || w.user?.email || w.user_id?.slice(0, 8) || "unknown")} <span className="text-white/40 text-xs">· {w.user?.email}</span>
               </div>
               <div className="mono text-[10px] text-white/50">{(w.created_at || "").slice(0, 16).replace("T", " ")} · {w.method} · {w.handle}</div>
             </div>
             <div className="text-right">
               <div className="font-black text-white text-lg">${Number(w.amount_usd).toFixed(2)}</div>
               <div className="mono text-[9px] text-white/50">fee ${Number(w.fee_usd).toFixed(2)} · payout ${Number(w.payout_usd).toFixed(2)}</div>
             </div>
             {w.status === "pending" ? (
               <div className="flex gap-2">
                 <button data-testid={`padmin-w-approve-${w.id}`} disabled={busyId === w.id} onClick={() => act(w.id, "approve")}
                   className="mono text-[10px] uppercase px-3 py-1.5 rounded-full bg-[#39FF14]/20 text-[#39FF14] hover:bg-[#39FF14]/30 border border-[#39FF14]/40 inline-flex items-center gap-1">
                   <CheckCircle2 className="w-3 h-3" /> Approve
                 </button>
                 <button data-testid={`padmin-w-reject-${w.id}`} disabled={busyId === w.id} onClick={() => act(w.id, "reject")}
                   className="mono text-[10px] uppercase px-3 py-1.5 rounded-full bg-[#FF003C]/20 text-[#FF003C] hover:bg-[#FF003C]/30 border border-[#FF003C]/40 inline-flex items-center gap-1">
                   <XCircle className="w-3 h-3" /> Reject
                 </button>
               </div>
             ) : (
               <div className="mono text-[10px] uppercase tracking-widest bg-white/5 border border-white/10 text-white/70 px-3 py-1 rounded-full">{w.status}</div>
             )}
           </div>
         ))}
       </div>}
    </div>
  );
}

function FraudPanel() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    powerAdminFraud().then((r) => setFlags(r.flags || [])).catch(() => toast.error("Couldn't load fraud signals")).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="text-white/50 text-sm">Loading…</div>;
  if (flags.length === 0) return <div data-testid="padmin-fraud-empty" className="glass rounded-xl p-8 text-center text-white/50"><ShieldCheck className="w-8 h-8 mx-auto text-[#39FF14] mb-2" />No suspicious activity in the last 24h.</div>;
  return (
    <div data-testid="padmin-fraud" className="space-y-2">
      <div className="text-white/60 text-xs mb-2">Flagging members who earned $2+ across 24h — investigate manually before banning.</div>
      {flags.map((f) => (
        <div key={f.user_id} data-testid={`padmin-fraud-${f.user_id}`} className="glass rounded-xl p-4 flex flex-wrap items-center gap-3">
          <ShieldAlert className="w-5 h-5 text-[#F59E0B]" />
          <div className="min-w-0 flex-1">
            <div className="font-heading font-bold text-white truncate">{f.name || f.email || f.user_id.slice(0, 8)} <span className="text-white/40 text-xs">· {f.email}</span></div>
            <div className="mono text-[10px] text-white/50">
              earned ${f.earned_last_24h_usd.toFixed(2)} · {f.actions_last_24h} actions · balance ${f.balance_usd.toFixed(2)} · lifetime ${f.lifetime_usd.toFixed(2)}
              {f.referred_by && <> · referred by <span className="text-cyan-400">{f.referred_by.slice(0, 8)}</span></>}
            </div>
          </div>
          {f.is_banned && <div className="mono text-[10px] uppercase bg-[#FF003C]/20 border border-[#FF003C]/40 text-[#FF003C] px-3 py-1 rounded-full inline-flex items-center gap-1"><Ban className="w-3 h-3" /> banned</div>}
        </div>
      ))}
    </div>
  );
}
